import React from 'react'
import { Form, Input, Select, Switch } from 'antd'
import { Box, Button, Divider, Typography } from '@mui/material'
import SaveIcon from '@mui/icons-material/Save'
import PaletteIcon from '@mui/icons-material/Palette'

import { ImageUrlInput } from '../SalepageDetail/ImageUrlInput'
import { ColorPickerInput } from '../SalepageDetail/ColorPickerInput'
import { GradientInput } from '../SalepageDetail/GradientInput'
import type { SalepageThemeValues } from '../SalepageDetail/SeoThemeTab'

const BG_SIZE_OPTIONS = [
  { value: 'cover', label: 'cover' },
  { value: 'contain', label: 'contain' },
  { value: 'auto', label: 'auto' },
]

const BG_POSITION_OPTIONS = [
  { value: 'top left', label: 'top left' },
  { value: 'top center', label: 'top center' },
  { value: 'top right', label: 'top right' },
  { value: 'center left', label: 'center left' },
  { value: 'center center', label: 'center center' },
  { value: 'center right', label: 'center right' },
  { value: 'bottom left', label: 'bottom left' },
  { value: 'bottom center', label: 'bottom center' },
  { value: 'bottom right', label: 'bottom right' },
]

const BG_REPEAT_OPTIONS = [
  { value: 'no-repeat', label: 'no-repeat' },
  { value: 'repeat', label: 'repeat' },
]

const THAI_FONT_PRESETS = [
  { value: 'default', label: 'ระบบ (Default)', fontFamily: '', fontUrl: '' },
  { value: 'sarabun', label: 'Sarabun', fontFamily: 'Sarabun, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Sarabun:wght@400;600;700&display=swap' },
  { value: 'prompt', label: 'Prompt', fontFamily: 'Prompt, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Prompt:wght@400;600;700&display=swap' },
  { value: 'kanit', label: 'Kanit', fontFamily: 'Kanit, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Kanit:wght@400;600;700&display=swap' },
  { value: 'bai-jamjuree', label: 'Bai Jamjuree', fontFamily: 'Bai Jamjuree, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Bai+Jamjuree:wght@400;600;700&display=swap' },
  { value: 'mitr', label: 'Mitr', fontFamily: 'Mitr, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Mitr:wght@400;600;700&display=swap' },
  { value: 'ibm-plex-sans-thai', label: 'IBM Plex Sans Thai', fontFamily: '"IBM Plex Sans Thai", sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=IBM+Plex+Sans+Thai:wght@400;600;700&display=swap' },
  { value: 'athiti', label: 'Athiti', fontFamily: 'Athiti, sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Athiti:wght@400;600;700&display=swap' },
  { value: 'chakra-petch', label: 'Chakra Petch', fontFamily: '"Chakra Petch", sans-serif', fontUrl: 'https://fonts.googleapis.com/css2?family=Chakra+Petch:wght@400;600;700&display=swap' },
]

interface PreviewThemeFormProps {
  initialTheme?: SalepageThemeValues
  onChange: (theme: SalepageThemeValues) => void
  onSave?: (theme: SalepageThemeValues) => Promise<void>
  saving?: boolean
}

export const PreviewThemeForm: React.FC<PreviewThemeFormProps> = ({
  initialTheme,
  onChange,
  onSave,
  saving,
}) => {
  const [form] = Form.useForm()
  const formValues = Form.useWatch([], form)
  const [useGradient, setUseGradient] = React.useState(!!initialTheme?.backgroundGradient)

  const fontUrl = formValues?.fontUrl
  const fontFamily = formValues?.fontFamily
  const backgroundColor = formValues?.backgroundColor
  const backgroundImage = formValues?.backgroundImage
  const backgroundSize = formValues?.backgroundSize
  const backgroundPosition = formValues?.backgroundPosition
  const backgroundRepeat = formValues?.backgroundRepeat
  const backgroundGradient = formValues?.backgroundGradient

  React.useEffect(() => {
    if (initialTheme) {
      form.setFieldsValue(initialTheme)
      setUseGradient(!!initialTheme.backgroundGradient)
    }
  }, [initialTheme, form])

  React.useEffect(() => {
    if (useGradient && backgroundGradient === '') setUseGradient(false)
  }, [useGradient, backgroundGradient])

  React.useEffect(() => {
    if (!formValues || typeof formValues !== 'object') return
    const clean = Object.fromEntries(
      Object.entries(formValues).filter(([, v]) => v != null && v !== ''),
    ) as SalepageThemeValues
    onChange(clean)
  }, [formValues, onChange])

  React.useEffect(() => {
    if (!fontUrl || !fontUrl.startsWith('https://fonts.googleapis.com')) return
    const id = 'salepage-preview-drawer-font'
    let link = document.getElementById(id) as HTMLLinkElement | null
    if (!link) {
      link = document.createElement('link')
      link.id = id
      link.rel = 'stylesheet'
      document.head.appendChild(link)
    }
    link.href = fontUrl
    return () => link?.remove()
  }, [fontUrl])

  const handleFontPresetChange = (value: string | undefined) => {
    if (!value || value === 'default') {
      form.setFieldsValue({ fontFamily: undefined, fontUrl: undefined })
      return
    }
    const preset = THAI_FONT_PRESETS.find((p) => p.value === value)
    if (preset) form.setFieldsValue({ fontFamily: preset.fontFamily, fontUrl: preset.fontUrl })
  }

  const matchedPreset = React.useMemo(() => {
    if (!fontFamily && !fontUrl) return 'default'
    if (!fontFamily || !fontUrl) return undefined
    return THAI_FONT_PRESETS.find(
      (p) => p.value !== 'default' && (p.fontUrl === fontUrl || fontFamily?.includes(p.label)),
    )?.value
  }, [fontFamily, fontUrl])

  const handleSave = async () => {
    if (!onSave) return
    const values = form.getFieldsValue() as SalepageThemeValues
    const clean = Object.fromEntries(
      Object.entries(values ?? {}).filter(([, v]) => v != null && v !== ''),
    ) as SalepageThemeValues
    await onSave(clean)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <PaletteIcon color="primary" />
        <Typography variant="subtitle1" fontWeight={700}>
          Theme
        </Typography>
      </Box>
      <Form form={form} layout="vertical" initialValues={initialTheme} style={{ flex: 1, overflow: 'auto' }}>
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Font
        </Typography>
        <Form.Item label="Preset ฟอนต์ไทย">
          <Select
            placeholder="เลือก preset"
            allowClear
            value={matchedPreset}
            options={THAI_FONT_PRESETS.map((p) => ({ value: p.value, label: p.label }))}
            onChange={handleFontPresetChange}
            size="small"
            getPopupContainer={() => document.body}
            dropdownStyle={{ zIndex: 9999 }}
          />
        </Form.Item>
        <Form.Item name="fontFamily" label="Font Family">
          <Input placeholder="Sarabun, Prompt" allowClear size="small" />
        </Form.Item>
        <Form.Item name="fontUrl" label="Google Fonts URL">
          <Input placeholder="https://fonts.googleapis.com/..." allowClear size="small" />
        </Form.Item>
        <Form.Item name="fontColor" label="Font Color">
          <ColorPickerInput placeholder="#333333" />
        </Form.Item>
        <Divider sx={{ my: 2 }} />
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          Background
        </Typography>
        <Form.Item name="backgroundColor" label="Background Color">
          <ColorPickerInput placeholder="#ffffff" />
        </Form.Item>
        <Form.Item name="backgroundImage" label="Background Image">
          <ImageUrlInput placeholder="https://..." />
        </Form.Item>
        {backgroundImage && (
          <>
            <Form.Item name="backgroundSize" label="Background Size">
              <Select
                placeholder="cover"
                allowClear
                options={BG_SIZE_OPTIONS}
                size="small"
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 9999 }}
              />
            </Form.Item>
            <Form.Item name="backgroundPosition" label="Background Position">
              <Select
                placeholder="top left, ..."
                allowClear
                options={BG_POSITION_OPTIONS}
                size="small"
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 9999 }}
              />
            </Form.Item>
            <Form.Item name="backgroundRepeat" label="Background Repeat">
              <Select
                placeholder="no-repeat"
                allowClear
                options={BG_REPEAT_OPTIONS}
                size="small"
                getPopupContainer={() => document.body}
                dropdownStyle={{ zIndex: 9999 }}
              />
            </Form.Item>
          </>
        )}
        <Form.Item label="Background Gradient">
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
            <Box
              role="button"
              tabIndex={0}
              onClick={() => {
                if (useGradient) {
                  setUseGradient(false)
                  form.setFieldValue('backgroundGradient', undefined)
                } else {
                  setUseGradient(true)
                  form.setFieldValue('backgroundGradient', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')
                }
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  if (useGradient) {
                    setUseGradient(false)
                    form.setFieldValue('backgroundGradient', undefined)
                  } else {
                    setUseGradient(true)
                    form.setFieldValue('backgroundGradient', 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)')
                  }
                }
              }}
              sx={{ display: 'flex', alignItems: 'center', gap: 1, cursor: 'pointer' }}
            >
              <Switch
                size="small"
                checked={useGradient}
                onChange={(checked) => {
                  setUseGradient(checked)
                  form.setFieldValue('backgroundGradient', checked ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)' : undefined)
                }}
              />
              <Typography variant="body2">{useGradient ? 'กำหนด gradient' : 'ไม่กำหนด'}</Typography>
            </Box>
            {useGradient && (
              <Form.Item name="backgroundGradient" noStyle>
                <GradientInput />
              </Form.Item>
            )}
          </Box>
        </Form.Item>
      </Form>
      {onSave && (
        <Button
          variant="contained"
          fullWidth
          disabled={saving}
          startIcon={<SaveIcon />}
          onClick={handleSave}
          sx={{ mt: 2 }}
        >
          {saving ? 'กำลังบันทึก...' : 'บันทึก Theme'}
        </Button>
      )}
    </Box>
  )
}
